/**
 * Metrics collection utility with provider abstraction.
 * Currently uses Sentry.metrics, but can be swapped for another provider.
 *
 * Design principles:
 * - Use increment counters only (cheapest metric type for free tiers)
 * - No high-cardinality tags (no IDs, user names, timestamps)
 * - Normalize paths to avoid cardinality explosion
 */

import * as Sentry from '@sentry/react';

// ============================================================================
// Types
// ============================================================================

export type EntityType = 'company' | 'site' | 'unit' | 'contact' | 'activity';

export interface MetricsProvider {
  increment(name: string, value?: number, tags?: Record<string, string>): void;
  gauge(name: string, value: number, tags?: Record<string, string>): void;
  flush(): Promise<void>;
}

// ============================================================================
// Providers
// ============================================================================

class SentryMetricsProvider implements MetricsProvider {
  increment(name: string, value = 1, tags?: Record<string, string>): void {
    try {
      // Sentry v10+ uses metrics.count() with attributes instead of tags
      Sentry.metrics.count(name, value, { attributes: tags });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[metrics] Failed to send increment', error);
      }
    }
  }

  gauge(name: string, value: number, tags?: Record<string, string>): void {
    try {
      // Sentry v10+ uses attributes instead of tags
      Sentry.metrics.gauge(name, value, { attributes: tags });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[metrics] Failed to send gauge', error);
      }
    }
  }

  async flush(): Promise<void> {
    try {
      await Sentry.flush(2000);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[metrics] Failed to flush', error);
      }
    }
  }
}

class NoopMetricsProvider implements MetricsProvider {
  increment(_name: string, _value?: number, _tags?: Record<string, string>): void {
    // No-op
  }

  gauge(_name: string, _value: number, _tags?: Record<string, string>): void {
    // No-op
  }

  async flush(): Promise<void> {
    // No-op
  }
}

// ============================================================================
// Provider Selection
// ============================================================================

function createProvider(): MetricsProvider {
  // Use Sentry if DSN is configured
  if (import.meta.env.VITE_SENTRY_DSN) {
    return new SentryMetricsProvider();
  }
  return new NoopMetricsProvider();
}

/**
 * Singleton metrics provider instance.
 * Automatically selects Sentry or no-op based on configuration.
 */
export const metrics: MetricsProvider = createProvider();

// ============================================================================
// Path Normalization
// ============================================================================

/**
 * Normalize a URL path to prevent high-cardinality tags.
 * Replaces UUIDs and numeric IDs with placeholders.
 *
 * Examples:
 * - /companies/abc-123-def → /companies/:id
 * - /reports/456/edit → /reports/:id/edit
 */
function normalizePath(path: string): string {
  return (
    path
      // Replace UUIDs (8-4-4-4-12 format)
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
      // Replace any remaining alphanumeric IDs in path segments (at least 8 chars)
      .replace(/\/[a-z0-9]{8,}(?=\/|$)/gi, '/:id')
      // Replace numeric IDs
      .replace(/\/\d+(?=\/|$)/g, '/:id')
  );
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Track a page view with normalized path.
 */
export function trackPageView(path: string): void {
  const normalizedPath = normalizePath(path);
  metrics.increment('page_view', 1, { path: normalizedPath });

  if (import.meta.env.DEV) {
    console.info('[metrics] page_view', { path: normalizedPath });
  }
}

/**
 * Track entity creation.
 */
export function trackEntityCreated(entity: EntityType): void {
  metrics.increment('entity_created', 1, { entity });

  if (import.meta.env.DEV) {
    console.info('[metrics] entity_created', { entity });
  }
}

/**
 * Track entity update.
 */
export function trackEntityUpdated(entity: EntityType): void {
  metrics.increment('entity_updated', 1, { entity });

  if (import.meta.env.DEV) {
    console.info('[metrics] entity_updated', { entity });
  }
}

/**
 * Track entity deletion.
 */
export function trackEntityDeleted(entity: EntityType): void {
  metrics.increment('entity_deleted', 1, { entity });

  if (import.meta.env.DEV) {
    console.info('[metrics] entity_deleted', { entity });
  }
}

/**
 * Track new report draft creation.
 */
export function trackReportCreated(): void {
  metrics.increment('report_created');

  if (import.meta.env.DEV) {
    console.info('[metrics] report_created');
  }
}

/**
 * Track report finalization (draft → finalized).
 */
export function trackReportFinalized(): void {
  metrics.increment('report_finalized');

  if (import.meta.env.DEV) {
    console.info('[metrics] report_finalized');
  }
}

/**
 * Track PDF generation.
 */
export function trackReportPdfGenerated(): void {
  metrics.increment('report_pdf_generated');

  if (import.meta.env.DEV) {
    console.info('[metrics] report_pdf_generated');
  }
}
